import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Coins, TrendingUp, Lock, Megaphone, X } from 'lucide-react';
import BgnStakingSection from '@/components/BgnStakingSection';

function PoolCard({ pool, onStake }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const stake = async () => {
    setBusy(true);
    try {
      await api.post('/staking/stake', { pool_id: pool.pool_id, amount: parseFloat(amount) });
      toast.success(`Staked ${amount} ${pool.symbol}`);
      setOpen(false); setAmount('');
      onStake?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Stake failed'); }
    finally { setBusy(false); }
  };

  const isElite = pool.apy >= 30;
  return (
    <div className={`bg-[#181A20] border ${isElite ? 'border-[#FCD535]/40' : 'border-[#2B3139]'} p-5 hover:border-[#FCD535]/60 transition-colors relative overflow-hidden`}
      data-testid={`pool-card-${pool.pool_id}`}>
      {isElite && <div className="absolute top-0 left-0 right-0 h-[2px] gold-border" />}
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-white text-lg font-semibold">{pool.symbol}</div>
          <div className="text-xs text-[#848E9C]">{pool.name}</div>
        </div>
        {pool.type === 'fixed' ? <Lock className="w-4 h-4 text-[#FCD535]" /> : <TrendingUp className="w-4 h-4 text-[#0ECB81]" />}
      </div>
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-wider text-[#848E9C]">Estimated APY</div>
        <div className={`font-mono text-4xl font-medium tracking-tight ${isElite ? 'text-[#FCD535]' : 'text-white'}`}>
          {pool.apy.toFixed(1)}%
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
        <div>
          <div className="text-[#848E9C]">Duration</div>
          <div className="text-white font-mono">{pool.duration_days > 0 ? `${pool.duration_days} days` : 'Flexible'}</div>
        </div>
        <div>
          <div className="text-[#848E9C]">Min. Amount</div>
          <div className="text-white font-mono">{pool.min_amount} {pool.symbol}</div>
        </div>
      </div>
      {pool.announcement && (
        <div className="mt-4 bg-[#FCD535]/10 border-l-2 border-[#FCD535] px-3 py-2 text-xs text-[#FCD535] leading-relaxed flex items-start gap-2" data-testid={`pool-announcement-${pool.pool_id}`}>
          <Megaphone className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{pool.announcement}</span>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button className="w-full mt-5 bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm h-10"
            data-testid={`stake-btn-${pool.pool_id}`}>Stake Now</Button>
        </DialogTrigger>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm">
          <DialogHeader><DialogTitle>Stake {pool.symbol}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-[#B7BDC6]">
              APY: <span className="text-[#FCD535] font-mono">{pool.apy.toFixed(1)}%</span> ·
              Duration: <span className="font-mono">{pool.duration_days > 0 ? `${pool.duration_days}d` : 'Flexible'}</span>
            </div>
            <Input placeholder={`Amount in ${pool.symbol} (min ${pool.min_amount})`} value={amount} onChange={e=>setAmount(e.target.value)} type="number"
              className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535]"
              data-testid="stake-amount-input" />
            <Button onClick={stake} disabled={busy} className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold"
              data-testid="confirm-stake-btn">
              {busy ? 'Staking…' : 'Confirm Stake'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Staking() {
  const { user } = useAuth();
  const [pools, setPools] = useState([]);
  const [mine, setMine] = useState([]);

  const load = async () => {
    const p = await api.get('/staking/pools');
    setPools(p.data.pools);
    if (user) {
      try {
        const m = await api.get('/staking/mine');
        setMine(m.data.stakes);
      } catch (_e) { /* ignore */ }
    } else {
      setMine([]);
    }
  };
  useEffect(() => { load(); }, [user]);

  const unstake = async (stake_id) => {
    try {
      await api.post(`/staking/unstake/${stake_id}`);
      toast.success('Unstaked successfully');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Unstake failed'); }
  };

  const flexible = pools.filter(p => p.type === 'flexible');
  const fixed = pools.filter(p => p.type === 'fixed');

  // Announcement bell — collect pools with announcements, filter out dismissed ones
  const DISMISS_KEY = 'bgn.staking.dismissed_announcements';
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}'); } catch { return {}; }
  });
  const activeAnnouncements = pools.filter(p => {
    if (!p.announcement) return false;
    const key = `${p.pool_id}:${p.announcement_updated_at || ''}`;
    return !dismissed[key];
  });
  const dismissAnnouncement = (pool) => {
    const key = `${pool.pool_id}:${pool.announcement_updated_at || ''}`;
    const next = { ...dismissed, [key]: true };
    setDismissed(next);
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch {}
  };
  const dismissAll = () => {
    const next = { ...dismissed };
    activeAnnouncements.forEach(p => {
      next[`${p.pool_id}:${p.announcement_updated_at || ''}`] = true;
    });
    setDismissed(next);
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch {}
  };

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <h1 className="font-display text-white text-4xl font-bold tracking-tighter">Earn</h1>
        <p className="text-[#848E9C] text-sm mt-1">Stake your crypto and earn passive rewards. Up to 120% APY.</p>
      </div>

      {activeAnnouncements.length > 0 && (
        <div className="border-l-4 border-[#FCD535] bg-gradient-to-r from-[#FCD535]/10 to-transparent p-4 mb-6" data-testid="staking-announcements-banner">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex items-center gap-2 text-[#FCD535] font-semibold text-sm uppercase tracking-wider">
              <Megaphone className="w-4 h-4" />
              What's new
              <span className="text-[10px] bg-[#FCD535] text-[#0B0E11] px-1.5 py-0.5">{activeAnnouncements.length}</span>
            </div>
            <button onClick={dismissAll} className="text-[10px] text-[#848E9C] hover:text-white uppercase tracking-wider" data-testid="dismiss-all-announcements">
              Dismiss all
            </button>
          </div>
          <div className="space-y-2">
            {activeAnnouncements.map(p => (
              <div key={p.pool_id} className="flex items-start justify-between gap-3 bg-[#0B0E11]/40 border border-[#2B3139] p-3" data-testid={`announcement-${p.pool_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm">
                    <span className="text-[#FCD535] font-semibold">{p.symbol}</span>
                    <span className="text-[#848E9C] mx-2">·</span>
                    <span className="font-mono text-[#0ECB81]">{p.apy}% APY</span>
                    <span className="text-[#848E9C] mx-2">·</span>
                    <span className="text-[#B7BDC6]">{p.name}</span>
                  </div>
                  <div className="text-sm text-[#B7BDC6] mt-1">{p.announcement}</div>
                </div>
                <button onClick={() => dismissAnnouncement(p)} className="text-[#848E9C] hover:text-white p-1" data-testid={`dismiss-${p.pool_id}`}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <BgnStakingSection />

      <Tabs defaultValue="all">
        <TabsList className="bg-[#181A20] border border-[#2B3139] rounded-sm mb-6">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#FCD535] data-[state=active]:text-[#0B0E11] rounded-none px-4" data-testid="tab-all">All Pools</TabsTrigger>
          <TabsTrigger value="flexible" className="data-[state=active]:bg-[#FCD535] data-[state=active]:text-[#0B0E11] rounded-none px-4" data-testid="tab-flexible">Flexible</TabsTrigger>
          <TabsTrigger value="fixed" className="data-[state=active]:bg-[#FCD535] data-[state=active]:text-[#0B0E11] rounded-none px-4" data-testid="tab-fixed">Fixed Term</TabsTrigger>
          <TabsTrigger value="mine" className="data-[state=active]:bg-[#FCD535] data-[state=active]:text-[#0B0E11] rounded-none px-4" data-testid="tab-mine">My Stakes</TabsTrigger>
        </TabsList>
        <TabsContent value="all">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pools.map(p => <PoolCard key={p.pool_id} pool={p} onStake={load} />)}
          </div>
        </TabsContent>
        <TabsContent value="flexible">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {flexible.map(p => <PoolCard key={p.pool_id} pool={p} onStake={load} />)}
          </div>
        </TabsContent>
        <TabsContent value="fixed">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {fixed.map(p => <PoolCard key={p.pool_id} pool={p} onStake={load} />)}
          </div>
        </TabsContent>
        <TabsContent value="mine">
          <div className="border border-[#2B3139] bg-[#181A20]">
            <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
              <div className="col-span-2">Asset</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-2 text-right">Amount</div>
              <div className="col-span-2 text-right">APY</div>
              <div className="col-span-2 text-right">Rewards</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            {mine.length === 0 ? (
              <div className="p-8 text-center text-[#848E9C] text-sm">No active stakes.</div>
            ) : mine.map(s => (
              <div key={s.stake_id} className="grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 items-center">
                <div className="col-span-2 text-white">{s.symbol}</div>
                <div className="col-span-2 text-[#848E9C] capitalize">{s.type}{s.duration_days ? ` · ${s.duration_days}d` : ''}</div>
                <div className="col-span-2 text-right font-mono text-white">{s.amount.toFixed(4)}</div>
                <div className="col-span-2 text-right font-mono text-[#FCD535]">{s.apy.toFixed(1)}%</div>
                <div className="col-span-2 text-right font-mono text-[#0ECB81]">+{s.rewards_earned.toFixed(6)}</div>
                <div className="col-span-2 text-right">
                  {s.status === 'active' ? (
                    <button onClick={() => unstake(s.stake_id)} className="text-[#F6465D] text-xs hover:underline" data-testid={`unstake-${s.stake_id}`}>Unstake</button>
                  ) : <span className="text-[#848E9C] text-xs">Closed</span>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
